import React, { useState, useRef, useEffect, useCallback } from 'react';

interface ImageCropModalProps {
  imageFile: File;
  imageUrl: string;
  onConfirm: (croppedFile: File) => void;
  onCancel: () => void;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w' | null;

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  imageFile,
  imageUrl,
  onConfirm,
  onCancel,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [cropArea, setCropArea] = useState<CropArea>({ x: 50, y: 50, width: 200, height: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  // 加载图片
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImage(img);
      
      // 计算缩放比例以适应容器
      const container = containerRef.current;
      if (container) {
        const maxWidth = container.clientWidth - 40;
        const maxHeight = container.clientHeight - 120;
        const scaleX = maxWidth / img.width;
        const scaleY = maxHeight / img.height;
        const newScale = Math.min(scaleX, scaleY, 1);
        
        setScale(newScale);
        setImageSize({
          width: img.width * newScale,
          height: img.height * newScale,
        });

        // 设置初始裁剪区域为图片中心的50%
        const initialWidth = img.width * newScale * 0.5;
        const initialHeight = img.height * newScale * 0.5;
        setCropArea({
          x: (img.width * newScale - initialWidth) / 2,
          y: (img.height * newScale - initialHeight) / 2,
          width: initialWidth,
          height: initialHeight,
        });
      }
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // 绘制画布
  useEffect(() => {
    if (!image || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = imageSize.width;
    canvas.height = imageSize.height;

    // 绘制图片
    ctx.drawImage(image, 0, 0, imageSize.width, imageSize.height);

    // 绘制半透明遮罩
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, imageSize.width, imageSize.height);

    // 清除裁剪区域的遮罩
    ctx.clearRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height);
    ctx.drawImage(
      image,
      cropArea.x / scale,
      cropArea.y / scale,
      cropArea.width / scale,
      cropArea.height / scale,
      cropArea.x,
      cropArea.y,
      cropArea.width,
      cropArea.height
    );

    // 绘制裁剪框边框（蓝色，与小程序一致）
    ctx.strokeStyle = '#1976d2';
    ctx.lineWidth = 2;
    ctx.strokeRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height);

    // 绘制调整手柄
    ctx.fillStyle = '#1976d2';
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    
    // 四个角 - 圆形手柄
    const cornerSize = 10;
    [
      { x: cropArea.x, y: cropArea.y },
      { x: cropArea.x + cropArea.width, y: cropArea.y },
      { x: cropArea.x, y: cropArea.y + cropArea.height },
      { x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height }
    ].forEach(pos => {
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, cornerSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    });

    // 四条边中点 - 矩形手柄
    const edgeWidth = 20;
    const edgeHeight = 4;
    
    // 上边
    ctx.fillRect(cropArea.x + cropArea.width / 2 - edgeWidth / 2, cropArea.y - edgeHeight / 2, edgeWidth, edgeHeight);
    ctx.strokeRect(cropArea.x + cropArea.width / 2 - edgeWidth / 2, cropArea.y - edgeHeight / 2, edgeWidth, edgeHeight);
    
    // 下边
    ctx.fillRect(cropArea.x + cropArea.width / 2 - edgeWidth / 2, cropArea.y + cropArea.height - edgeHeight / 2, edgeWidth, edgeHeight);
    ctx.strokeRect(cropArea.x + cropArea.width / 2 - edgeWidth / 2, cropArea.y + cropArea.height - edgeHeight / 2, edgeWidth, edgeHeight);
    
    // 左边
    ctx.fillRect(cropArea.x - edgeHeight / 2, cropArea.y + cropArea.height / 2 - edgeWidth / 2, edgeHeight, edgeWidth);
    ctx.strokeRect(cropArea.x - edgeHeight / 2, cropArea.y + cropArea.height / 2 - edgeWidth / 2, edgeHeight, edgeWidth);
    
    // 右边
    ctx.fillRect(cropArea.x + cropArea.width - edgeHeight / 2, cropArea.y + cropArea.height / 2 - edgeWidth / 2, edgeHeight, edgeWidth);
    ctx.strokeRect(cropArea.x + cropArea.width - edgeHeight / 2, cropArea.y + cropArea.height / 2 - edgeWidth / 2, edgeHeight, edgeWidth);
  }, [image, cropArea, imageSize, scale]);

  // 获取鼠标位置相对于canvas的坐标
  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  // 获取触摸位置相对于canvas的坐标
  const getTouchPos = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || e.touches.length === 0) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    };
  };

  // 检测鼠标是否在调整手柄上
  const getResizeHandle = (x: number, y: number): ResizeHandle => {
    const handleSize = 10;
    const tolerance = 5;

    // 检测四个角
    if (Math.abs(x - cropArea.x) < handleSize + tolerance && Math.abs(y - cropArea.y) < handleSize + tolerance) return 'nw';
    if (Math.abs(x - (cropArea.x + cropArea.width)) < handleSize + tolerance && Math.abs(y - cropArea.y) < handleSize + tolerance) return 'ne';
    if (Math.abs(x - cropArea.x) < handleSize + tolerance && Math.abs(y - (cropArea.y + cropArea.height)) < handleSize + tolerance) return 'sw';
    if (Math.abs(x - (cropArea.x + cropArea.width)) < handleSize + tolerance && Math.abs(y - (cropArea.y + cropArea.height)) < handleSize + tolerance) return 'se';

    // 检测四条边
    if (Math.abs(x - (cropArea.x + cropArea.width / 2)) < handleSize + tolerance && Math.abs(y - cropArea.y) < handleSize + tolerance) return 'n';
    if (Math.abs(x - (cropArea.x + cropArea.width / 2)) < handleSize + tolerance && Math.abs(y - (cropArea.y + cropArea.height)) < handleSize + tolerance) return 's';
    if (Math.abs(x - cropArea.x) < handleSize + tolerance && Math.abs(y - (cropArea.y + cropArea.height / 2)) < handleSize + tolerance) return 'w';
    if (Math.abs(x - (cropArea.x + cropArea.width)) < handleSize + tolerance && Math.abs(y - (cropArea.y + cropArea.height / 2)) < handleSize + tolerance) return 'e';

    return null;
  };

  // 鼠标按下
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e);
    const handle = getResizeHandle(pos.x, pos.y);

    if (handle) {
      setIsResizing(true);
      setResizeHandle(handle);
      setDragStart(pos);
    } else if (
      pos.x >= cropArea.x &&
      pos.x <= cropArea.x + cropArea.width &&
      pos.y >= cropArea.y &&
      pos.y <= cropArea.y + cropArea.height
    ) {
      setIsDragging(true);
      setDragStart({ x: pos.x - cropArea.x, y: pos.y - cropArea.y });
    }
  };

  // 触摸开始
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const pos = getTouchPos(e);
    const handle = getResizeHandle(pos.x, pos.y);

    if (handle) {
      setIsResizing(true);
      setResizeHandle(handle);
      setDragStart(pos);
    } else if (
      pos.x >= cropArea.x &&
      pos.x <= cropArea.x + cropArea.width &&
      pos.y >= cropArea.y &&
      pos.y <= cropArea.y + cropArea.height
    ) {
      setIsDragging(true);
      setDragStart({ x: pos.x - cropArea.x, y: pos.y - cropArea.y });
    }
  };

  // 鼠标移动
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e);
    processCropAreaUpdate(pos);
  };

  // 触摸移动
  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const pos = getTouchPos(e);
    processCropAreaUpdate(pos);
  };

  // 处理裁剪区域更新（鼠标和触摸共用）
  const processCropAreaUpdate = (pos: { x: number; y: number }) => {
    if (isResizing && resizeHandle) {
      let newCropArea = { ...cropArea };

      switch (resizeHandle) {
        case 'nw':
          newCropArea.width = cropArea.width + (cropArea.x - pos.x);
          newCropArea.height = cropArea.height + (cropArea.y - pos.y);
          newCropArea.x = pos.x;
          newCropArea.y = pos.y;
          break;
        case 'ne':
          newCropArea.width = pos.x - cropArea.x;
          newCropArea.height = cropArea.height + (cropArea.y - pos.y);
          newCropArea.y = pos.y;
          break;
        case 'sw':
          newCropArea.width = cropArea.width + (cropArea.x - pos.x);
          newCropArea.height = pos.y - cropArea.y;
          newCropArea.x = pos.x;
          break;
        case 'se':
          newCropArea.width = pos.x - cropArea.x;
          newCropArea.height = pos.y - cropArea.y;
          break;
        case 'n':
          newCropArea.height = cropArea.height + (cropArea.y - pos.y);
          newCropArea.y = pos.y;
          break;
        case 's':
          newCropArea.height = pos.y - cropArea.y;
          break;
        case 'w':
          newCropArea.width = cropArea.width + (cropArea.x - pos.x);
          newCropArea.x = pos.x;
          break;
        case 'e':
          newCropArea.width = pos.x - cropArea.x;
          break;
      }

      // 限制最小尺寸
      if (newCropArea.width < 50) newCropArea.width = 50;
      if (newCropArea.height < 50) newCropArea.height = 50;

      // 限制在图片范围内
      if (newCropArea.x < 0) newCropArea.x = 0;
      if (newCropArea.y < 0) newCropArea.y = 0;
      if (newCropArea.x + newCropArea.width > imageSize.width) {
        newCropArea.width = imageSize.width - newCropArea.x;
      }
      if (newCropArea.y + newCropArea.height > imageSize.height) {
        newCropArea.height = imageSize.height - newCropArea.y;
      }

      setCropArea(newCropArea);
    } else if (isDragging) {
      let newX = pos.x - dragStart.x;
      let newY = pos.y - dragStart.y;

      // 限制在图片范围内
      newX = Math.max(0, Math.min(newX, imageSize.width - cropArea.width));
      newY = Math.max(0, Math.min(newY, imageSize.height - cropArea.height));

      setCropArea({ ...cropArea, x: newX, y: newY });
    } else {
      // 更新鼠标样式（仅鼠标模式）
      const handle = getResizeHandle(pos.x, pos.y);
      if (handle) {
        const cursors: Record<string, string> = {
          nw: 'nw-resize',
          ne: 'ne-resize',
          sw: 'sw-resize',
          se: 'se-resize',
          n: 'n-resize',
          s: 's-resize',
          w: 'w-resize',
          e: 'e-resize',
        };
        if (canvasRef.current) {
          canvasRef.current.style.cursor = cursors[handle];
        }
      } else if (
        pos.x >= cropArea.x &&
        pos.x <= cropArea.x + cropArea.width &&
        pos.y >= cropArea.y &&
        pos.y <= cropArea.y + cropArea.height
      ) {
        if (canvasRef.current) {
          canvasRef.current.style.cursor = 'move';
        }
      } else {
        if (canvasRef.current) {
          canvasRef.current.style.cursor = 'default';
        }
      }
    }
  };

  // 鼠标释放
  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  // 触摸结束
  const handleTouchEnd = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  // 裁剪图片并确认
  const handleConfirm = useCallback(async () => {
    if (!image) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 设置canvas尺寸为裁剪区域的原始尺寸
    canvas.width = cropArea.width / scale;
    canvas.height = cropArea.height / scale;

    // 绘制裁剪后的图片
    ctx.drawImage(
      image,
      cropArea.x / scale,
      cropArea.y / scale,
      cropArea.width / scale,
      cropArea.height / scale,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // 转换为Blob
    canvas.toBlob((blob) => {
      if (blob) {
        const croppedFile = new File([blob], imageFile.name, {
          type: imageFile.type,
          lastModified: Date.now(),
        });
        onConfirm(croppedFile);
      }
    }, imageFile.type);
  }, [image, cropArea, scale, imageFile, onConfirm]);

  // 重置裁剪区域
  const handleReset = useCallback(() => {
    if (!imageSize.width || !imageSize.height) return;
    
    // 重置为居中，占图片的60%
    const initialWidth = imageSize.width * 0.6;
    const initialHeight = imageSize.height * 0.6;
    setCropArea({
      x: (imageSize.width - initialWidth) / 2,
      y: (imageSize.height - initialHeight) / 2,
      width: initialWidth,
      height: initialHeight,
    });
  }, [imageSize]);

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* 标题区域 */}
      <div className="bg-black bg-opacity-80 text-center py-6 px-4">
        <h2 className="text-2xl font-semibold text-white mb-2">选择题目区域</h2>
        <p className="text-sm text-gray-300">拖动选择框选择要解答的题目</p>
      </div>
      
      {/* 图片裁剪区域 */}
      <div ref={containerRef} className="flex-1 flex items-center justify-center overflow-hidden">
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="max-w-full max-h-full cursor-move touch-none"
        />
      </div>

      {/* 底部按钮区域 */}
      <div className="bg-black bg-opacity-90 p-6 border-t border-white border-opacity-10">
        <div className="flex justify-between gap-4 max-w-2xl mx-auto">
          <button
            onClick={onCancel}
            className="flex-1 px-6 py-4 bg-white bg-opacity-20 text-white rounded-full hover:bg-opacity-30 transition-colors font-semibold text-lg"
          >
            取消
          </button>
          <button
            onClick={handleReset}
            className="flex-1 px-6 py-4 bg-white bg-opacity-30 text-white rounded-full hover:bg-opacity-40 transition-colors font-semibold text-lg"
          >
            重置
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-6 py-4 text-white rounded-full transition-colors font-semibold text-lg"
            style={{ backgroundColor: '#07c160' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#06ad56')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#07c160')}
          >
            确定
          </button>
        </div>
      </div>
    </div>
  );
};
